import { useEffect, useRef } from "react";
import PageLayout from "@/components/feature/PageLayout";
import DiagonalLines from "@/components/feature/DiagonalLines";
import HomeFooter from "@/pages/home/components/HomeFooter";

const companyInfo = [
  { label: "会社名", value: "株式会社クラプリックス" },
  { label: "所在地", value: "〒464-0075 愛知県名古屋市千種区内山3-10-19 若山ビル3F" },
  { label: "事業内容", value: null, list: ["WEBサイトの企画・制作・運用", "業務システムおよびアプリの企画・開発", "デジタルマーケティング支援（SEO/MEO/LLMO・広告運用）", "インフルエンサーマーケティング支援"] },
  { label: "営業時間", value: "10:00〜17:00" },
  { label: "定休日", value: "土・日・祝" },
];

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("revealed"); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

function BrandSection() {
  const leftRef = useReveal(0.15);

  return (
    <div className="mb-10 md:mb-16 flex flex-col md:flex-row items-start gap-8 md:gap-10">
      <div ref={leftRef} className="reveal flex-1">
        <span className="text-sm md:text-base tracking-[0.4em] font-medium block mb-5" style={{ fontFamily: "'Barlow', sans-serif", color: "#c00000" }}>BRAND STATEMENT</span>
        <h2 className="text-3xl md:text-4xl font-bold leading-snug mb-8" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>
          這い上がる先に、<br />幸運は待っている
        </h2>
        <p className="text-sm md:text-base leading-loose" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111", lineHeight: 2.8 }}>
          CRUPLIXという名前には、二つの言葉が宿っています。<br />
          <strong style={{ color: "#c00000" }}>crawl up</strong>——這い上がる。<strong style={{ color: "#c00000" }}>felix</strong>——ラテン語で、幸運。<br /><br />
          どんな状況からでも前を向き、泥臭く這い上がっていく。その先にこそ、本物の幸運は待っている。私たちはそう信じています。<br /><br />
          華やかな出発点など必要ありません。大切なのは、今いる場所から動き続けること。<br />
          CRUPLIXは、這い上がろうとするすべての人と事業のそばに立ち、その歩みを支え、共に幸運を掴みにいく会社です。
        </p>
      </div>
    </div>
  );
}

function CompanyInfoTable() {
  const titleRef = useReveal(0.1);

  return (
    <div>
      <div ref={titleRef} className="reveal mb-10">
        <h3 className="text-3xl font-bold" style={{ fontFamily: "'Barlow', sans-serif", color: "#111111" }}>会社概要</h3>
      </div>
      <div className="flex flex-col" style={{ borderTop: "1px solid #e0e0e0" }}>
        {companyInfo.map((item, idx) => (
          <InfoRow key={item.label} item={item} delay={idx * 80} />
        ))}
      </div>
    </div>
  );
}

function InfoRow({ item, delay }: { item: typeof companyInfo[0]; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateX(-20px)";
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setTimeout(() => {
          if (!el) return;
          el.style.transition = "opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)";
          el.style.opacity = "1";
          el.style.transform = "translateX(0)";
        }, delay);
        obs.unobserve(el);
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className="flex flex-col md:flex-row py-8 md:py-12 gap-2 md:gap-8 group cursor-default"
      style={{ borderBottom: "1px solid #e0e0e0", transition: "background 0.3s" }}
      onMouseEnter={e => (e.currentTarget.style.background = "#fafafa")}
      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
    >
      <div className="w-full sm:w-40 text-sm md:text-base font-medium shrink-0 tracking-widest pt-0.5" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>
        {item.label}
      </div>
      <div className="flex-1">
        {item.value && (
          <span className="text-sm md:text-base" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>{item.value}</span>
        )}
        {item.list && (
          <ul className="flex flex-col gap-6">
            {item.list.map((li, i) => (
              <li key={i} className="flex items-center gap-3 text-sm md:text-base" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>
                <span className="shrink-0" style={{ color: "#c00000" }}>
                  <i className="ri-checkbox-blank-circle-fill text-[6px]" />
                </span>
                {li}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AccessSection() {
  const ref = useReveal(0.1);
  return (
    <section className="px-4 md:px-6 lg:px-20 pb-16 md:pb-28" style={{ background: "#ffffff" }}>
      <div className="max-w-6xl mx-auto">
        <div ref={ref} className="reveal flex flex-col gap-10">
          <h3 className="text-2xl font-bold" style={{ fontFamily: "'Barlow', sans-serif", color: "#111111" }}>アクセス</h3>
          <p className="text-sm md:text-base" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>
            愛知県名古屋市千種区内山3-10-19 若山ビル3F
          </p>
          <div
            className="w-full overflow-hidden"
            style={{ height: "clamp(280px, 40vw, 480px)", border: "1px solid #e0e0e0" }}
          >
            <iframe
              src="https://maps.google.com/maps?q=35.1702545,136.9363651&output=embed&z=18&hl=ja"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="株式会社クラプリックス 所在地マップ 愛知県名古屋市千種区内山3-10-19"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function CompanyPage() {
  const sectionRef = useReveal(0.05);

  return (
    <PageLayout>
      <section className="py-16 md:py-28 px-4 md:px-6 lg:px-20" style={{ background: "#ffffff" }}>
        <div className="max-w-6xl mx-auto">
          <BrandSection />
          <div ref={sectionRef} className="reveal">
            <CompanyInfoTable />
          </div>
        </div>
      </section>
      <AccessSection />
      <HomeFooter />
      <DiagonalLines variant="gold" intensity="light" />
    </PageLayout>
  );
}
